import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useExperienceWizard } from 'core/contexts/experienceWizard';
import { getMoodsIds, getTranslatedName, moods, validateOptionalString, validateRequiredString } from '@utriper/core';
import { Box, FormHelperText, Grid2 } from '@mui/material';
import { Container, Label } from '../../styles';
import { Field, Form } from 'react-final-form';
import Title from 'modules/Pricing/components/Title';
import ActionButtons from 'modules/Pricing/PriceGroupWizard/components/ActionButtons';
import { CharacterCounter, SelectCheckbox, Textarea, TextInput, Switch } from '@utriper/core-ui';

const ExperienceDataForm = () => {
    const history = useHistory();
    const { i18n, t } = useTranslation();
    const translate = (key) => t(`experience_form.${key}`);
    const { stepData, handleNext, resetWizard, updateStepData, STEP_KEYS } = useExperienceWizard();
    const [submitted, setSubmitted] = useState(false);

    const modusesOptions = useMemo(
        () =>
            getMoodsIds().map((mood) => ({
                id: mood,
                name: getTranslatedName(moods, mood, i18n.language),
            })),
        [i18n.language]
    );

    const handleBack = (event) => {
        event?.preventDefault();
        resetWizard();
        history.push('/experience');
    };

    const handleSubmit = (values) => {
        updateStepData(STEP_KEYS.STEP_1, values);
        handleNext();
    };

    const validateForm = (values) => {
        const errors = {};

        if (!validateRequiredString(values?.name)) {
            errors.name = t('common_form.required');
        }

        if (!validateOptionalString(values?.description1)) {
            errors.description1 = t('common_form.max_length');
        }

        if (!validateOptionalString(values?.description2, 3000)) {
            errors.description2 = t('common_form.max_length');
        }

        return errors;
    };

    return (
        <Form
            onSubmit={handleSubmit}
            validate={validateForm}
            initialValues={stepData[STEP_KEYS.STEP_1]}
            render={({ handleSubmit, form, values, errors }) => (
                <form onSubmit={handleSubmit} noValidate style={{ width: '100%', maxWidth: '480px' }}>
                    <Box component="section">
                        <Title title={translate('experience_data.title')} description={translate('experience_data.description')} />

                        {/* Form */}
                        <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                            {/* Name */}
                            <Container>
                                <Label>{translate('name')}</Label>
                                <Field
                                    name="name"
                                    component={TextInput}
                                    placeholder={translate('name_placeholder')}
                                    slotProps={{
                                        input: {
                                            inputProps: {
                                                maxLength: 255,
                                            },
                                        },
                                    }}
                                />
                            </Container>

                            {/* Description1 (subtitle) */}
                            <Container>
                                <Label>{translate('subtitle')}</Label>
                                <Field
                                    name="description1"
                                    component={TextInput}
                                    placeholder={translate('subtitle_placeholder')}
                                    slotProps={{
                                        input: {
                                            inputProps: {
                                                maxLength: 255,
                                            },
                                        },
                                    }}
                                />
                            </Container>

                            {/* Description2 (description) */}
                            <Container>
                                <Label>{translate('description')}</Label>
                                <Field
                                    name="description2"
                                    component={Textarea}
                                    placeholder={translate('description_placeholder')}
                                    minRows={4}
                                    slotProps={{
                                        input: {
                                            inputProps: {
                                                maxLength: 3000,
                                            },
                                        },
                                    }}
                                />
                                <CharacterCounter length={values?.description2?.length ?? 0} maxLength={3000} />
                            </Container>

                            {/* Moduses */}
                            <Container>
                                <Label>{translate('moods')}</Label>
                                <Field
                                    name="moduses"
                                    component={SelectCheckbox}
                                    options={modusesOptions}
                                    values={values?.moduses ?? []}
                                    label={translate('moods_placeholder')}
                                    hasError={submitted && errors?.moduses}
                                    onChange={(newValues) => {
                                        form.change('moduses', [...newValues]);
                                    }}
                                />
                                {submitted && errors?.moduses && <FormHelperText error>{errors.moduses}</FormHelperText>}
                            </Container>

                            {/* Time Period */}
                            <Container>
                                <Label>{translate('time_period_label')}</Label>
                                <Field id="timePeriod" name="timePeriod" type="checkbox" component={Switch} label={translate('time_period_label')} />
                                <FormHelperText>{translate('time_period_helper')}</FormHelperText>
                            </Container>
                        </Grid2>

                        <ActionButtons
                            handleSubmit={(e) => {
                                setSubmitted(true);
                                return handleSubmit(e);
                            }}
                            handleBack={handleBack}
                        />
                    </Box>
                </form>
            )}
        />
    );
};

export default ExperienceDataForm;
